import { Languages } from 'lucide-react';
import type { Language, Translations } from '../types';

interface HeaderProps {
  t: Translations;
  language: Language;
  onToggleLanguage: () => void;
  /**
   * Sprint 81 BUG-81-08 §4: kept in props for API stability (App.tsx still
   * passes it) but Header no longer renders a separate "Comenzar" CTA.
   * Single auth CTA is `onSignIn` only.
   */
  onGetStarted?: () => void;
  onSignIn: () => void;
  isMaintenanceMode?: boolean;
  /** Manos F7-P2: when true, render the "Venezuela" crisis nav item (status dot → F1). */
  manosEnabled?: boolean;
}

// Sprint 81 BUG-81-08 §1: unified logo source. Single source-of-truth for the
// header + footer logo (was previously a lucide-Globe icon + text).
const LOGO_SRC = '/assets/QUUL_Logo.png';

export function Header({ t, language, onToggleLanguage, onSignIn, isMaintenanceMode = false, manosEnabled = false }: HeaderProps) {
  return (
    <header className="border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo — Sprint 81 BUG-81-08 follow-up: logo image replaces the
            "Quul" wordmark; sized to ~3x the previous logo dimensions so
            visual presence matches what the wordmark+small-icon combo
            previously occupied.

            #1967 R5: the BETA badge that used to sit beside the logo is
            removed outright (founder decision, 2026-09-05) — not reworded and
            not made conditional. Header.test.tsx asserts its ABSENCE so it
            cannot return unnoticed. */}
        <div className="flex items-center gap-2 sm:gap-3">
          <img
            src={LOGO_SRC}
            alt="Quul"
            className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg object-contain"
          />
        </div>

        {/* Actions — Sprint 81 BUG-81-08 §4: single auth CTA. Previously two
            buttons ("Iniciar Sesión" + "Comenzar") with the same behaviour;
            collapsed to one CTA per architect ratification. */}
        <div className="flex items-center gap-1 sm:gap-3">
          {manosEnabled && (
            <a
              href="/venezuela"
              data-testid="manos-nav-item"
              className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors text-xs sm:text-sm font-medium text-vinotinto-dark"
            >
              <span className="w-2 h-2 rounded-full bg-vinotinto animate-pulse" aria-hidden="true" />
              {t.manos.navItem}
            </a>
          )}
          <button
            onClick={onToggleLanguage}
            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Languages className="w-4 h-4" />
            <span className="text-xs sm:text-sm font-medium">{language === 'en' ? 'ES' : 'EN'}</span>
          </button>
          <button
            onClick={isMaintenanceMode ? undefined : onSignIn}
            disabled={isMaintenanceMode}
            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base transition-colors ${
              isMaintenanceMode
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-vinotinto text-white hover:bg-vinotinto-dark'
            }`}
          >
            {isMaintenanceMode ? t.comingSoon : t.signIn}
          </button>
        </div>
      </div>
    </header>
  );
}
